import type { ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { scrollToAnchor } from '@/lib/scrollToAnchor'
import { localeFromPath, localizedPath } from '@/i18n/locale'
import { homeBase, isHomePath, pathForAnchor } from '@/lib/sections'

/**
 * Универсальный nav-link, понимающий разницу между:
 *   - якорем блока, у которого есть свой адрес (#calculator → /roi, см.
 *     src/lib/sections.ts) → ссылка ведёт на этот адрес: строка браузера
 *     меняется, и её можно скопировать. На главной клик ещё и сам
 *     прокручивает — иначе повторный клик по тому же пункту не делал бы
 *     ничего (адрес уже стоит, роутеру нечего менять).
 *   - якорем без адреса (#top, #lead-form) → на главной плавный скролл;
 *     вне главной — router-навигация на «/#anchor», ScrollToHash доскроллит.
 *   - статичный путь в public/ (/club, /privacy) → обычный <a href> с full
 *     page reload, иначе React Router intercept'ит клик и показывает 404
 *     (этих путей нет в routes.tsx — они живут в public/<path>/index.html).
 *   - React-роут (/guides, /blog/*, /digital/*) → router <Link> для SPA-перехода.
 *
 * Используется в Nav, Footer, StickyCta и любых других UI-блоках.
 * Гарантирует что переходы не «зависают» на внутренних страницах.
 */
type Props = {
  href: string
  className?: string
  onClick?: () => void
  children: ReactNode
  ariaLabel?: string
}

// Пути, обслуживаемые статически из public/ — НЕ React-routes.
// Добавлять сюда КАЖДЫЙ новый путь, который кладёшь как public/<path>/index.html.
const STATIC_PATHS = ['/privacy', '/developer']

function isStaticPath(href: string): boolean {
  return STATIC_PATHS.some((p) => href === p || href.startsWith(`${p}/`))
}

export function NavLink({ href, className, onClick, children, ariaLabel }: Props) {
  const { pathname, search } = useLocation()
  const locale = localeFromPath(pathname)
  const isHome = isHomePath(pathname)
  const isHash = href.startsWith('#')

  /**
   * Сначала даём отработать onClick вызывающего, и СИНХРОННО: мобильное меню
   * лежит в потоке внутри sticky-шапки, и пока оно открыто, страница на
   * ~500px длиннее. Без flushSync цель считалась при открытом меню, экран
   * перелетал блок на высоту меню и через секунду рывком возвращался.
   */
  const scrollAfter = (anchor: string) => {
    flushSync(() => onClick?.())
    scrollToAnchor(anchor)
  }

  if (isHash) {
    const anchor = href.slice(1)
    // «Наверх» на главной = адрес самой главной: с /roi возвращает «/»,
    // иначе перезагрузка или «поделиться» снова увезли бы к калькулятору.
    const sectionPath = anchor === 'top' ? homeBase(pathname) : pathForAnchor(anchor, locale)
    if (sectionPath && (anchor !== 'top' || isHome)) {
      // search переносим как есть: в нём живёт ?utm_offer рекламного
      // посетителя, и без него первый экран на глазах менялся бы с оффера
      // из объявления на обычный.
      // state.scrolled — пометка для ScrollToHash: прокрутку уже запустил
      // клик, второй раз стартовать не надо (две анимации дрались бы).
      return (
        <Link
          to={{ pathname: sectionPath, search }}
          state={isHome ? { scrolled: true } : undefined}
          className={className}
          aria-label={ariaLabel}
          onClick={() => {
            if (isHome) scrollAfter(anchor)
            else onClick?.()
          }}
        >
          {children}
        </Link>
      )
    }
    if (isHome) {
      // Прямой скролл на каждый клик: на длинной странице нативный hash-jump
      // и смена hash → ScrollToHash дают «недоезд», а повторный клик (hash
      // уже стоит) не делает ничего. preventDefault + scrollToAnchor чинит оба.
      return (
        <a
          href={href}
          className={className}
          aria-label={ariaLabel}
          onClick={(e) => {
            e.preventDefault()
            scrollAfter(anchor)
          }}
        >
          {children}
        </a>
      )
    }
    // Вне главной: навигируем на главную В ТЕКУЩЕЙ ЛОКАЛИ + hash
    // (ru → /#anchor, en → /en#anchor). ScrollToHash потом доскроллит.
    const home = localizedPath('/', locale)
    return (
      <Link
        to={`${home}${href}`}
        className={className}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    )
  }

  // Статичные страницы из public/ — нужен ПРИНУДИТЕЛЬНЫЙ full reload.
  // ViteReactSSG использует data-router (react-router v6.4+), который
  // глобально intercept'ит даже обычные <a> внутри RouterProvider.
  // Без preventDefault + window.location.assign React Router пытается
  // найти /club в routes.tsx, не находит и показывает «Unexpected
  // Application Error! 404 Not Found».
  //
  // assign с trailing slash — чтобы избежать лишнего 308-редиректа от
  // CF Pages (/privacy → /privacy/).
  if (isStaticPath(href)) {
    return (
      <a
        href={href}
        className={className}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.preventDefault()
          onClick?.()
          const target = href.endsWith('/') ? href : `${href}/`
          window.location.assign(target)
        }}
      >
        {children}
      </a>
    )
  }

  // React-route — SPA-переход через Link.
  return (
    <Link to={href} className={className} onClick={onClick} aria-label={ariaLabel}>
      {children}
    </Link>
  )
}
